import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { io } from "socket.io-client";
import { setAuthToken } from "../lib/api";

/** Listen only while My trips is mounted; proof forms keep their own state. */
export function useOperatorTripUpdates(operatorId: string | null) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!operatorId) return;
    let disposed = false;
    const refresh = () => {
      void queryClient.invalidateQueries({
        queryKey: ["operator-trips", operatorId],
        exact: true,
      });
    };
    const socket = io(import.meta.env.VITE_API_URL || undefined, {
      transports: ["websocket"],
      autoConnect: false,
      // Refresh auth on every connection attempt. The server derives the
      // operator room from this token; never subscribe with a claimed ID.
      auth: (complete) => {
        void getAccessTokenSilently()
          .then((token) => {
            if (disposed) return;
            setAuthToken(token);
            complete({ token });
          })
          .catch(() => {
            if (!disposed) socket.disconnect();
          });
      },
    });
    socket.on("connect", refresh); // Recover events missed while disconnected.
    socket.on("trip:assigned", refresh);
    socket.on("trip:updated", refresh); // Includes reassignment/unassignment.
    socket.on("trip:deleted", refresh);
    const reconnect = () => {
      if (document.visibilityState === "visible" && !socket.connected) {
        socket.connect();
      }
    };
    document.addEventListener("visibilitychange", reconnect);
    socket.connect();

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", reconnect);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [operatorId, getAccessTokenSilently, queryClient]);
}
